import React, { useEffect, useMemo, useRef, useState } from "react";
import Modal from "react-modal";
import { FiChevronDown, FiX } from "react-icons/fi";
import { FileText, Monitor } from "lucide-react";
import { renderSettingsOptions } from "../ProjectV2/Render/render.constants";
import "./CreateCustomProjectModal.css";

Modal.setAppElement("#root");

const PROJECT_TYPES = [
  {
    value: "scroll",
    label: "Scroll",
    description: "Continuous vertical roll",
    Icon: FileText,
  },
  {
    value: "card",
    label: "Cards",
    description: "Static title cards",
    Icon: Monitor,
  },
];

const RESOLUTION_OPTIONS = Object.keys(renderSettingsOptions);

export default function CreateCustomProjectModal({
  isOpen,
  closeModal,
  projectName,
  setProjectName,
  projectType,
  setProjectType,
  resolution,
  setResolution,
  onCreate,
  creating = false,
  /** When set ("scroll" | "card"), opposite type is disabled (template flow). */
  lockedProjectType = null,
}) {
  const [resolutionOpen, setResolutionOpen] = useState(false);
  const resolutionRef = useRef(null);

  const resolutionOptions = useMemo(() => RESOLUTION_OPTIONS, []);
  const isTypeLocked =
    lockedProjectType === "scroll" || lockedProjectType === "card";

  useEffect(() => {
    if (!isOpen || !isTypeLocked) return;
    if (projectType !== lockedProjectType) {
      setProjectType(lockedProjectType);
    }
  }, [isOpen, isTypeLocked, lockedProjectType, projectType, setProjectType]);

  useEffect(() => {
    if (projectType !== "card") {
      setResolutionOpen(false);
    }
  }, [projectType]);

  useEffect(() => {
    if (!resolutionOpen) return undefined;

    const handleClickOutside = (event) => {
      if (!resolutionRef.current?.contains(event.target)) {
        setResolutionOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setResolutionOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [resolutionOpen]);

  const handleKeyDown = (e) => {
    if (
      e.key === "Enter" &&
      (e.code === "Enter" || e.code === "NumpadEnter") &&
      projectName.trim() &&
      !creating
    ) {
      onCreate();
    }
  };

  const handleClose = () => {
    setProjectType("scroll");
    setResolutionOpen(false);
    closeModal();
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      style={{
        overlay: {
          backgroundColor: "rgba(6, 5, 12, 0.72)",
          backdropFilter: "blur(8px)",
          zIndex: 100,
        },
        content: {
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(480px, calc(100vw - 32px))",
          maxHeight: "calc(100vh - 32px)",
          padding: 0,
          border: "1px solid rgba(124, 92, 255, 0.25)",
          borderRadius: "20px",
          background: "#12101c",
          overflow: "visible",
          outline: "none",
        },
      }}
      className="create-custom-project-modal"
    >
      <div className="relative p-6 md:p-7">
        <button
          type="button"
          onClick={handleClose}
          className="absolute top-5 right-5 p-2 rounded-full text-[#625e83] hover:text-white hover:bg-[rgba(255,255,255,0.06)] transition-colors"
          aria-label="Close"
        >
          <FiX className="w-4 h-4" />
        </button>

        <div className="pr-8 mb-6">
          <h2 className="font-body text-[22px] font-semibold text-white tracking-tight">
            Create custom project
          </h2>
          <p className="font-body text-[14px] text-[#a8a4c8] mt-1.5 leading-relaxed">
            Start from scratch with a blank canvas.
          </p>
        </div>

        <div className="space-y-5">
          <div>
            <label
              htmlFor="custom-project-name"
              className="block font-body text-[13px] text-white mb-2"
            >
              Project Name
            </label>
            <input
              id="custom-project-name"
              name="ccp-project-name"
              type="text"
              autoFocus
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              data-lpignore="true"
              data-form-type="other"
              value={projectName}
              placeholder="e.g. Indie Short Film"
              onChange={(e) => setProjectName(e.target.value)}
              onKeyDown={handleKeyDown}
              className="ccp-input font-body"
            />
          </div>

          <div>
            <p className="font-body text-[13px] text-white mb-2">Project Type</p>
            <div className="grid grid-cols-2 gap-3">
              {PROJECT_TYPES.map(({ value, label, description, Icon }) => {
                const isSelected = projectType === value;
                const isDisabled = isTypeLocked && value !== lockedProjectType;
                return (
                  <button
                    key={value}
                    type="button"
                    disabled={isDisabled}
                    aria-disabled={isDisabled}
                    title={
                      isDisabled
                        ? `This template is a ${
                            lockedProjectType === "card" ? "card" : "scroll"
                          } project`
                        : undefined
                    }
                    onClick={() => {
                      if (!isDisabled) setProjectType(value);
                    }}
                    className={`ccp-type-card ${
                      isSelected ? "is-selected" : ""
                    } ${isDisabled ? "is-disabled" : ""}`}
                  >
                    <Icon
                      className={`w-5 h-5 mb-3 ${
                        isDisabled
                          ? "text-[#4a4668]"
                          : isSelected
                            ? "text-[#c4b2ff]"
                            : "text-[#a8a4c8]"
                      }`}
                      strokeWidth={1.75}
                    />
                    <div
                      className={`font-body text-[15px] font-medium ${
                        isDisabled
                          ? "text-[#4a4668]"
                          : isSelected
                            ? "text-white"
                            : "text-[#f0eeff]"
                      }`}
                    >
                      {label}
                    </div>
                    <div
                      className={`font-body text-[12px] mt-1 leading-snug ${
                        isDisabled ? "text-[#3d3a55]" : "text-[#625e83]"
                      }`}
                    >
                      {description}
                    </div>
                  </button>
                );
              })}
            </div>
            {isTypeLocked && (
              <p className="font-body text-[12px] text-[#625e83] mt-2 leading-snug">
                Type is fixed to{" "}
                {lockedProjectType === "card" ? "Cards" : "Scroll"} for this
                template.
              </p>
            )}
          </div>

          {projectType === "card" && (
            <div>
              <label
                htmlFor="ccp-resolution-trigger"
                className="block font-body text-[13px] text-white mb-2"
              >
                Resolution
              </label>
              <div className="ccp-resolution" ref={resolutionRef}>
                <button
                  id="ccp-resolution-trigger"
                  type="button"
                  className={`ccp-resolution-trigger font-body ${
                    resolutionOpen ? "is-open" : ""
                  }`}
                  onClick={() => setResolutionOpen((prev) => !prev)}
                  aria-haspopup="listbox"
                  aria-expanded={resolutionOpen}
                >
                  <span
                    className={
                      resolution ? "ccp-resolution-value" : "ccp-resolution-placeholder"
                    }
                  >
                    {resolution || "Select resolution"}
                  </span>
                  <FiChevronDown
                    className={`ccp-resolution-chevron ${
                      resolutionOpen ? "is-open" : ""
                    }`}
                  />
                </button>

                {resolutionOpen && (
                  <div className="ccp-resolution-menu" role="listbox">
                    {resolutionOptions.map((option) => {
                      const isActive = resolution === option;
                      return (
                        <button
                          key={option}
                          type="button"
                          role="option"
                          aria-selected={isActive}
                          className={`ccp-resolution-option font-body ${
                            isActive ? "is-active" : ""
                          }`}
                          onClick={() => {
                            setResolution(option);
                            setResolutionOpen(false);
                          }}
                        >
                          {option}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={onCreate}
          disabled={!projectName.trim() || creating}
          style={creating ? { background: "var(--sx-dim, #a8a4c8)" } : undefined}
          className={`w-full mt-7 py-3 rounded-xl font-body text-[15px] font-semibold transition-all disabled:cursor-not-allowed ${
            creating
              ? "text-[#12101c]"
              : "bg-[#7c5cff] text-white hover:bg-[#9d7dff] disabled:opacity-40 hover:enabled:-translate-y-px hover:enabled:shadow-[0_8px_24px_-8px_rgba(124,92,255,0.6)]"
          }`}
        >
          {creating ? "Creating Project..." : "Create Project"}
        </button>
      </div>
    </Modal>
  );
}
